import { useSortable } from '@dnd-kit/sortable';
import { Box, createStyles, Group } from '@mantine/core';
import {
	ITEM_GAP,
	VERTICAL_INDICATOR_AFTER_ID_PREFIX,
	VERTICAL_INDICATOR_BEFORE_ID_PREFIX,
} from './constants';
import { DnDVerticalIndicator } from './DnDVerticalIndicator';
import { type DnDItem, type RenderItemArgs } from './types';

interface DnDItemProps<T> {
	item: DnDItem<T>;
	width: string;
	isActive: boolean;
	isFirstItem: boolean;
	isLastItem: boolean;
	isHoveringOverSameRow: boolean;
	isIndicatorDisabled: boolean;
	isOverlay?: boolean;
	readOnly?: boolean;
	renderItem: (args: RenderItemArgs<T>) => React.ReactNode;
	onResizeStart?: (indicatorId: string, mouseX: number) => void;
}

const useStyles = <T,>() =>
	createStyles(
		(
			theme,
			{
				isActive,
				isFirstItem,
				isLastItem,
				isOverlay,
			}: Omit<
				DnDItemProps<T>,
				| 'item'
				| 'isHoveringOverSameRow'
				| 'isIndicatorDisabled'
				| 'width'
				| 'renderItem'
			>
		) => {
			let paddingToSubtract = ITEM_GAP;

			if (isFirstItem && !isLastItem) {
				paddingToSubtract = ITEM_GAP / 2;
			}
			if (isLastItem && !isFirstItem) {
				paddingToSubtract = ITEM_GAP + ITEM_GAP / 2;
			}
			if (isOverlay) {
				paddingToSubtract = 0;
			}

			return {
				wrapper: {
					position: 'relative',
					width: `calc(100% - ${paddingToSubtract}px)`,
					opacity: isActive || isOverlay ? 0.6 : 1,
				},
			};
		}
	);

export function DnDItemInternal<T>({
	item,
	isActive,
	isFirstItem,
	isLastItem,
	isOverlay = false,
	readOnly = false,
	renderItem,
}: Omit<
	DnDItemProps<T>,
	'isHoveringOverSameRow' | 'isIndicatorDisabled' | 'width'
>) {
	const { index, listeners, isDragging, setNodeRef, setActivatorNodeRef } =
		useSortable({
			id: item.id,
			disabled: readOnly,
		});

	const { classes } = useStyles<T>()({
		isFirstItem,
		isLastItem,
		isActive,
		isOverlay,
	});

	return (
		<Box ref={setNodeRef} className={classes.wrapper}>
			{renderItem({
				activatorRef: setActivatorNodeRef,
				index,
				item,
				listeners: readOnly ? undefined : listeners,
				isDragging: isOverlay || isDragging,
			})}
		</Box>
	);
}

export function DnDItem<T>({
	item,
	width,
	isActive,
	isFirstItem,
	isLastItem,
	isIndicatorDisabled,
	isHoveringOverSameRow,
	readOnly = false,
	renderItem,
	onResizeStart,
}: DnDItemProps<T>) {
	return (
		<Group w={width} spacing={0} align="stretch">
			<DnDVerticalIndicator
				id={`${VERTICAL_INDICATOR_BEFORE_ID_PREFIX}-${item.id}`}
				isFirstItem={isFirstItem}
				isLastItem={isLastItem}
				isHoveringOverSameRow={isHoveringOverSameRow}
				disabled={isIndicatorDisabled}
				readOnly={readOnly}
				onResizeStart={onResizeStart}
			/>
			<DnDItemInternal<T>
				item={item}
				isActive={isActive}
				isFirstItem={isFirstItem}
				isLastItem={isLastItem}
				readOnly={readOnly}
				renderItem={renderItem}
			/>
			{isLastItem && (
				<DnDVerticalIndicator
					id={`${VERTICAL_INDICATOR_AFTER_ID_PREFIX}-${item.id}`}
					isFirstItem={isFirstItem}
					isLastItem={isLastItem}
					isHoveringOverSameRow={isHoveringOverSameRow}
					disabled={isIndicatorDisabled}
					readOnly={readOnly}
					onResizeStart={onResizeStart}
				/>
			)}
		</Group>
	);
}
